/**
 * The upgrade pitch, raised by any gate that refuses.
 *
 * Ported from the legacy app's MarketingModal shell: rocket, "Upgrade to
 * <Plan>" with the plan in an accent colour, a lead paragraph, a callout with
 * an icon, a checklist of what the tier adds, and one primary action to the
 * plan page. The checklist is derived (see upgrade-plan-gains), so it cannot
 * promise something the plans do not sell.
 *
 * It pitches the CHEAPEST tier on sale that solves the refusal. Being refused
 * Key Stats dilution on Basic offers Pro, not the top of the catalog.
 */
import { useEffect } from 'react'
import { Check, Rocket } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { BETA } from '@/config/constants'
import { usePlanConstraint } from '@/context/plan-constraint-hooks'
import { FEATURE_LABELS, IS_MOCK_AUTH, PLAN_LABELS } from '@/context/plan-constraint-utils'
import { useGetSubscriptionPlansQuery } from '@/services/api/users-api'
import { trackEvent, EVENTS } from '@/services/analytics'
import { useNotificationStore } from '@/stores/notification-store'
import { UPGRADE_CONTENT, UPGRADE_FALLBACK, type UpgradeCopyContext } from './upgrade-catalog'
import { featuresGained, limitsGained, onSaleTiers } from './upgrade-plan-gains'
import { useUpgradePromptStore } from './upgrade-prompt-store'
import { resolveTargetTier, unavailableMessage } from './upgrade-dialog-messages'
import { SUBSCRIBED_BILLING_PATH } from '@/features/auth/subscription-state'

/** A modal that scrolls stops being read. */
const MAX_LISTED_GAINS = 12

export function UpgradeDialog() {
  const request = useUpgradePromptStore((s) => s.request)
  const close = useUpgradePromptStore((s) => s.closeUpgradePrompt)
  const { planTier } = usePlanConstraint()
  const navigate = useNavigate()
  // Fetched only once a gate refuses; until it answers the default list (what
  // the server sells today) stands in. Beta has no billing page to send to.
  const { data: plans } = useGetSubscriptionPlansQuery(undefined, {
    skip: !request || BETA || IS_MOCK_AUTH,
  })

  const targetTier =
    request && !BETA ? resolveTargetTier(request, planTier, onSaleTiers(plans)) : null

  // Fires per OPEN, not per render: the request object identity changes only
  // when a gate raises a new prompt.
  useEffect(() => {
    if (!request || !targetTier) return
    trackEvent(EVENTS.UPGRADE_PROMPT_SHOWN, {
      feature: request.feature,
      reason: request.reason ?? 'locked',
      target_tier: targetTier,
    })
  }, [request, targetTier])

  // Nothing to sell: no tier on sale solves the refusal, or this is beta, where
  // there is no billing page. Silence would be worse than the toast this
  // replaced, because the user clicked and got no answer at all, so say what
  // happened and drop the request.
  useEffect(() => {
    if (!request || targetTier) return
    useNotificationStore.getState().showInfo(unavailableMessage(request))
    close()
  }, [request, targetTier, close])

  if (!request || !targetTier) return null

  const entry = UPGRADE_CONTENT[request.feature] ?? UPGRADE_FALLBACK
  const copy: UpgradeCopyContext = {
    currentPlanLabel: PLAN_LABELS[planTier],
    targetPlanLabel: PLAN_LABELS[targetTier],
    limit: request.context?.limit,
    label: request.context?.label,
  }
  const lead = request.reason === 'limit' && entry.limit ? entry.limit(copy) : entry.locked(copy)
  const Icon = entry.icon
  // The refused feature is the headline; repeating it as a bullet reads as
  // padding. Long jumps can gain a dozen things, and a modal that scrolls stops
  // being read, so the tail is counted rather than listed.
  const allGains = [
    ...featuresGained(planTier, targetTier)
      .filter((f) => f !== request.feature)
      .map((f) => FEATURE_LABELS[f]),
    ...limitsGained(planTier, targetTier).map((l) => l.text),
  ]
  const gains = allGains.slice(0, MAX_LISTED_GAINS)
  const unlisted = allGains.length - gains.length

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[460px] max-w-[92vw] gap-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Rocket className="h-5 w-5 text-primary" aria-hidden />
            <span>
              Upgrade to <span className="text-primary">{copy.targetPlanLabel}</span>
            </span>
          </DialogTitle>
          <DialogDescription className="pt-1 text-sm leading-relaxed">{lead}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 rounded-md border border-border/60 bg-muted/30 p-3">
          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <div>
            <p className="text-sm font-semibold">{entry.calloutTitle(copy)}</p>
            <p className="pt-1 text-sm leading-relaxed text-muted-foreground">
              {entry.calloutBody(copy)}
            </p>
          </div>
        </div>

        {gains.length > 0 && (
          <div>
            <p className="pb-1.5 text-sm text-muted-foreground">
              {copy.targetPlanLabel} also includes:
            </p>
            <ul className="space-y-1">
              {gains.map((text) => (
                <li key={text} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            {unlisted > 0 && (
              <p className="pt-1.5 pl-6 text-sm text-muted-foreground">and {unlisted} more</p>
            )}
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          onClick={() => {
            close()
            navigate(SUBSCRIBED_BILLING_PATH)
          }}
        >
          Upgrade now
        </Button>
      </DialogContent>
    </Dialog>
  )
}
